/**
 * AllDataFields.js
 * Single source of truth for ALL entity fields used in the client list view.
 * Merges: Primary fields (from ClientFields) + KYC fields (from UploadDetailsFields)
 */

import {
  validateEmail,
  validatePAN,
  validateGST,
  validateMobile,
  validateIFSC,
  validateAccountNumber,
} from "../../utils/validationUtils";
import {
  COMPANY_OPTIONS,
  PARENT_CHILD_MAP,
} from "../../constants/companyDistributorData";
import {
  LOB_OPTIONS_ALL,
  DEPT_OPTIONS_BASE,
} from "../CreateNew/CreateNewForm/Clients/ClientFields";
import React, { useState } from "react";

// ── Section 1: Primary Details ─────────────────────────────────────────────────
export const PRIMARY_FIELDS = [
  {
    name: "parentCode",
    label: "Company",
    type: "select",
    required: false,
    section: "primary",
    options: COMPANY_OPTIONS,
  },

  {
    name: "entityCategory",
    label: "Entity Category",
    type: "select",
    required: true,
    section: "primary",
    options: [
      { value: "", label: "Select entity category" },
      { value: "Service Partner", label: "Service Partner" },
      { value: "client", label: "Client" },
    ],
  },
  {
    name: "EntityName",
    label: "Entity Name",
    type: "text",
    placeholder: "Please enter entity name",
    required: true,
    section: "primary",
  },
  {
    name: "entityLOBType",
    label: "Entity LOB Type",
    type: "select",
    required: true,
    section: "primary",
    options: LOB_OPTIONS_ALL, // overridden dynamically based on entityCategory
  },

  {
    name: "aaDepartmentFunction",
    label: "AA - Department Function",
    type: "select",
    required: true,
    section: "primary",
    options: DEPT_OPTIONS_BASE, // overridden dynamically based on entityCategory + LOB
  },
  {
    name: "contactPersonEmail",
    label: "E-mail Address",
    type: "email",
    placeholder: "Enter email address",
    required: true,
    section: "primary",
    validation: validateEmail,
  },
  {
    name: "spocEmailAddress",
    label: "SPOC E-mail Address",
    type: "email",
    placeholder: "Enter SPOC email address",
    required: true,
    section: "primary",
    validation: validateEmail,
  },
];

// ── Section 2: KYC / Entity Details ───────────────────────────────────────────
export const KYC_ENTITY_FIELDS = [
  {
    name: "parentName",
    label: "Entity Name",
    type: "text",
    placeholder: "Enter entity name",
    required: true,
  },
  {
    name: "tradeName",
    label: "Trade Name",
    type: "text",
    placeholder: "Enter trade name",
    required: true,
  },
  {
    name: "pinCode",
    label: "Pin Code",
    type: "text",
    placeholder: "Enter 6-digit pin code",
    required: true,
    maxLength: 6,
  },
  {
    name: "city",
    label: "City",
    type: "text",
    placeholder: "Auto-filled from pincode",
    readOnly: true,
  },
  {
    name: "State",
    label: "State",
    type: "text",
    placeholder: "Auto-filled from pincode",
    readOnly: true,
  },
  {
    name: "address",
    label: "Address",
    type: "textarea",
    placeholder: "Enter complete address",
    rows: 3,
  },
  {
    name: "natureOfBusiness",
    label: "Nature of Business",
    type: "textarea",
    placeholder: "Brief description",
    required: true,
  },
  {
    name: "entityType",
    label: "Entity Type",
    type: "select",
    required: true,
    options: [
      { value: "", label: "Select entity type" },
      { value: "proprietorship", label: "Proprietorship" },
      { value: "partnership", label: "Partnership" },
      { value: "llp", label: "Limited Liability Partnership (LLP)" },
      { value: "private_limited", label: "Private Limited Company" },
      { value: "public_limited", label: "Public Limited Company" },
      { value: "trust", label: "Trust" },
      { value: "society", label: "Society" },
    ],
  },
  {
    name: "CompanypanNumber",
    label: "PAN Number",
    type: "text",
    placeholder: "e.g. ABCDE1234F",
    required: true,
    maxLength: 10,
    validation: validatePAN,
  },
];

// ── Section 3: GST ─────────────────────────────────────────────────────────────
export const KYC_GST_FIELDS = [
  {
    name: "isGSTRegistered",
    label: "GST Registered?",
    type: "select",
    required: true,
    options: [
      { value: "", label: "Select option" },
      { value: "yes", label: "Yes" },
      { value: "no", label: "No" },
    ],
  },
];

// ── Section 4: MSME ────────────────────────────────────────────────────────────
export const KYC_MSME_FIELDS = [
  {
    name: "isMSMERegistered",
    label: "Registered under MSME?",
    type: "select",
    required: true,
    options: [
      { value: "", label: "Select option" },
      { value: "yes", label: "Yes" },
      { value: "no", label: "No" },
    ],
  },
];

// ── Section 5: Contact Person ──────────────────────────────────────────────────
export const KYC_CONTACT_FIELDS = [
  {
    name: "contactPersonName",
    label: "Contact Person Name",
    type: "text",
    placeholder: "Enter name",
    required: true,
  },
  {
    name: "contactPersonDesignation",
    label: "Designation",
    type: "text",
    placeholder: "Enter designation",
    required: true,
  },
  {
    name: "contactPersonMobile",
    label: "Mobile Number",
    type: "tel",
    placeholder: "10-digit mobile",
    required: true,
    maxLength: 10,
    validation: validateMobile,
  },
];

// ── Section 6: Bank Details ────────────────────────────────────────────────────
export const KYC_BANK_FIELDS = [
  {
    name: "bankName",
    label: "Bank Name",
    type: "text",
    placeholder: "Enter bank name",
    required: true,
  },
  {
    name: "accountNumber",
    label: "Account Number",
    type: "text",
    placeholder: "Enter account number",
    required: true,
    validation: validateAccountNumber,
  },
  {
    name: "ifscCode",
    label: "IFSC Code",
    type: "text",
    placeholder: "e.g. SBIN0001234",
    required: true,
    maxLength: 11,
    validation: validateIFSC,
  },
  {
    name: "accountType",
    label: "Account Type",
    type: "select",
    required: true,
    options: [
      { value: "", label: "Select account type" },
      { value: "savings", label: "Savings" },
      { value: "current", label: "Current" },
    ],
  },
  {
    name: "branchName",
    label: "Branch Name",
    type: "text",
    placeholder: "Enter branch name",
    required: true,
  },
  {
    name: "beneficiaryName",
    label: "Beneficiary Name",
    type: "text",
    placeholder: "Enter beneficiary name",
    required: true,
  },
];

// ── All fields combined (for full form / listing detail view) ──────────────────
export const ALL_DATA_FIELDS = [
  ...PRIMARY_FIELDS,
  ...KYC_ENTITY_FIELDS,
  ...KYC_GST_FIELDS,
  ...KYC_MSME_FIELDS,
  ...KYC_CONTACT_FIELDS,
  ...KYC_BANK_FIELDS,
];

// ── Re-exports for convenience ─────────────────────────────────────────────────
export { PARENT_CHILD_MAP, validateGST };

// ── Required documents per condition ──────────────────────────────────────────

/**
 * Documents required when GST is registered (isGSTRegistered === "yes")
 */
export const DOCS_GST_YES = [
  { key: "doc_gst_certificate", label: "GST Certificate", required: true },
];

/**
 * Documents required when GST is NOT registered (isGSTRegistered === "no")
 * Utility bill as address proof
 */
export const DOCS_GST_NO = [
  {
    key: "doc_utility_bill",
    label: "Utility Bill (Address Proof)",
    required: true,
  },
];

/**
 * Documents required when MSME is registered (isMSMERegistered === "yes")
 */
export const DOCS_MSME_YES = [
  {
    key: "doc_msme_certificate",
    label: "MSME Registration Certificate",
    required: true,
  },
];

/**
 * Documents required when bank details are provided (hasBankDetails === "yes")
 */
export const DOCS_BANK = [
  {
    key: "doc_bank_proof",
    label: "Bank Document (Passbook / Cancelled Cheque)",
    required: true,
  },
];

/**
 * Documents required per entity type.
 * Key = entityType value, value = array of { key, label, required }
 */
export const DOCS_BY_ENTITY_TYPE = {
  proprietorship: [
    {
      key: "doc_aadhar",
      label: "Aadhar Card",
      required: true,
      inputField: {
        name: "aadharCardNumber",
        placeholder: "Enter Aadhar number",
        maxLength: 12,
      },
    },
    {
      key: "doc_pan",
      label: "PAN Card",
      required: true,
      inputField: {
        name: "panCardNumber",
        placeholder: "Enter PAN number",
        maxLength: 10,
      },
    },
  ],
  partnership: [
    {
      key: "doc_partnershipDeed",
      label: "Partnership Deed",
      required: true,
      inputField: {
        name: "partnershipDeedNumber",
        placeholder: "Enter deed reference / date",
      },
    },
    {
      key: "doc_aadharBoth",
      label: "Aadhar of Both Partners",
      required: true,
      inputField: {
        name: "aadharNumberOfBothPartners",
        placeholder: "Enter Aadhar numbers",
      },
    },
    {
      key: "doc_pan",
      label: "PAN Card",
      required: true,
      inputField: {
        name: "panCardNumber",
        placeholder: "Enter PAN number",
        maxLength: 10,
      },
    },
  ],
  llp: [
    {
      key: "doc_partnershipDeed",
      label: "Partnership Deed",
      required: true,
      inputField: {
        name: "partnershipDeedNumber",
        placeholder: "Enter deed reference / date",
      },
    },
    {
      key: "doc_aadharBoth",
      label: "Aadhar of Both Partners",
      required: true,
      inputField: {
        name: "aadharNumberOfBothPartners",
        placeholder: "Enter Aadhar numbers ",
      },
    },
    {
      key: "doc_pan",
      label: "PAN Card",
      required: true,
      inputField: {
        name: "panCardNumber",
        placeholder: "Enter PAN number",
        maxLength: 10,
      },
    },
  ],
  private_limited: [
    {
      key: "doc_coi",
      label: "Certificate of Incorporation (COI)",
      required: true,
      inputField: {
        name: "certificateOfIncorporationNumber",
        placeholder: "Enter COI number",
      },
    },
    {
      key: "doc_pan",
      label: "PAN Card",
      required: true,
      inputField: {
        name: "panCardNumber",
        placeholder: "Enter PAN number",
        maxLength: 10,
      },
    },
    {
      key: "doc_directorPan",
      label: "Director PAN",
      required: true,
      inputField: {
        name: "directorPANNumber",
        placeholder: "Enter Director PAN number",
        maxLength: 10,
      },
    },
    {
      key: "doc_directorAadhar",
      label: "Director Aadhar",
      required: true,
      inputField: {
        name: "directorAadharNumber",
        placeholder: "Enter Director Aadhar number",
        maxLength: 12,
      },
    },
  ],
  public_limited: [
    {
      key: "doc_coi",
      label: "Certificate of Incorporation (COI)",
      required: true,
      inputField: {
        name: "certificateOfIncorporationNumber",
        placeholder: "Enter COI number",
      },
    },
    {
      key: "doc_pan",
      label: "PAN Card",
      required: true,
      inputField: {
        name: "panCardNumber",
        placeholder: "Enter PAN number",
        maxLength: 10,
      },
    },
    {
      key: "doc_directorPan",
      label: "Director PAN",
      required: false,
      inputField: {
        name: "directorPANNumber",
        placeholder: "Enter Director PAN number",
        maxLength: 10,
      },
    },
    {
      key: "doc_directorAadhar",
      label: "Director Aadhar",
      required: false,
      inputField: {
        name: "directorAadharNumber",
        placeholder: "Enter Director Aadhar number",
        maxLength: 12,
      },
    },
  ],
  trust: [
    {
      key: "doc_trustDeed",
      label: "Trust Deed",
      required: true,
      inputField: {
        name: "trustDeedNumber",
        placeholder: "Enter trust deed reference / date",
      },
    },
    {
      key: "doc_panTrust",
      label: "PAN of Trust",
      required: true,
      inputField: {
        name: "panOfTrustNumber",
        placeholder: "Enter Trust PAN number",
        maxLength: 10,
      },
    },
    {
      key: "doc_aadharTrustee",
      label: "Aadhar of Trustee",
      required: true,
      inputField: {
        name: "aadharNumberOfTrustee",
        placeholder: "Enter Trustee Aadhar number",
        maxLength: 12,
      },
    },
    {
      key: "doc_panTrustee",
      label: "PAN of Trustee",
      required: false,
      inputField: {
        name: "panOfTrusteeNumber",
        placeholder: "Enter Trustee PAN number",
        maxLength: 10,
      },
    },
  ],
  society: [
    {
      key: "doc_pan",
      label: "PAN Card",
      required: true,
      inputField: {
        name: "panCardNumber",
        placeholder: "Enter PAN number",
        maxLength: 10,
      },
    },
    {
      key: "doc_aadhar",
      label: "Aadhar Card",
      required: true,
      inputField: {
        name: "aadharCardNumber",
        placeholder: "Enter Aadhar number",
        maxLength: 12,
      },
    },
  ],
};

/**
 * Helper — returns the full list of required documents for a given form state.
 * Combines entity-type docs + conditional GST/MSME docs.
 *
 * @param {object} formData - current form state
 * @returns {Array<{ key, label, required }>}
 */
export const getRequiredDocuments = (formData) => {
  const docs = [];

  // Entity-type documents
  const entityDocs = DOCS_BY_ENTITY_TYPE[formData.entityType] || [];
  docs.push(...entityDocs);

  // GST conditional documents
  if (formData.isGSTRegistered === "yes") docs.push(...DOCS_GST_YES);
  if (formData.isGSTRegistered === "no") docs.push(...DOCS_GST_NO);

  // MSME conditional documents
  if (formData.isMSMERegistered === "yes") docs.push(...DOCS_MSME_YES);

  // Bank documents
  if (formData.hasBankDetails === "yes") docs.push(...DOCS_BANK);

  // Deduplicate by key
  const seen = new Set();
  return docs.filter((d) => {
    if (seen.has(d.key)) return false;
    seen.add(d.key);
    return true;
  });
};

// ── Document upload sections (mirrors KYC form behaviour) ─────────────────────
// NOTE: These are used ONLY in the all-listing view. No other file is changed.

/**
 * Single document upload slot — shows filename when uploaded, dashed box otherwise.
 */
export const DocUploadSlot = ({
  docKey,
  label,
  required,
  uploadedFile,
  onChange,
}) => {
  const hasFile = !!uploadedFile;
  return (
    <div className="al-doc-slot">
      <label className="al-doc-slot__label">
        {label}
        {required && <span className="al-doc-slot__req"> *</span>}
      </label>
      <label
        htmlFor={`doc-upload-${docKey}`}
        className={`al-doc-slot__box ${hasFile ? "al-doc-slot__box--done" : ""}`}
      >
        <input
          id={`doc-upload-${docKey}`}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          className="al-doc-slot__input"
          onChange={(e) => onChange(docKey, e.target.files[0])}
        />
        {hasFile ? (
          <span className="al-doc-slot__filename">✓ {uploadedFile.name}</span>
        ) : (
          <>
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="16 16 12 12 8 16" />
              <line x1="12" y1="12" x2="12" y2="21" />
              <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
            </svg>
            <span className="al-doc-slot__hint">Click to upload</span>
            <span className="al-doc-slot__types">PDF, JPG, PNG</span>
          </>
        )}
      </label>
    </div>
  );
};

/**
 * DocUploadSection — renders document upload slots based on current form state.
 * Shows:
 *  - Entity-type docs (when entityType is selected)
 *  - GST certificate (when isGSTRegistered === "yes")
 *  - Utility bill (when isGSTRegistered === "no")
 *  - MSME certificate (when isMSMERegistered === "yes")
 *
 * Props:
 *  formData   — current KYC form state (entityType, isGSTRegistered, isMSMERegistered)
 *  docFiles   — { [docKey]: File } map of uploaded files
 *  onDocChange — (docKey, file) => void
 */
export const DocUploadSection = ({
  formData = {},
  docFiles = {},
  onDocChange,
}) => {
  const docs = getRequiredDocuments(formData);
  if (docs.length === 0) return null;

  // Group: entity-type docs vs conditional docs
  const entityTypeDocs = DOCS_BY_ENTITY_TYPE[formData.entityType] || [];
  const entityTypeKeys = new Set(entityTypeDocs.map((d) => d.key));
  const conditionalDocs = docs.filter((d) => !entityTypeKeys.has(d.key));

  return (
    <div className="al-doc-section">
      {/* Entity type documents */}
      {entityTypeDocs.length > 0 && (
        <div className="al-doc-group">
          <p className="al-doc-group__title">
            Documents for{" "}
            <strong style={{ textTransform: "capitalize" }}>
              {formData.entityType?.replace(/_/g, " ")}
            </strong>
          </p>
          <div className="al-doc-grid">
            {entityTypeDocs.map((doc) => (
              <DocUploadSlot
                key={doc.key}
                docKey={doc.key}
                label={doc.label}
                required={doc.required}
                uploadedFile={docFiles[doc.key] || null}
                onChange={onDocChange}
              />
            ))}
          </div>
        </div>
      )}

      {/* Conditional documents (GST / MSME) */}
      {conditionalDocs.length > 0 && (
        <div className="al-doc-group">
          <p className="al-doc-group__title">Additional Documents</p>
          <div className="al-doc-grid">
            {conditionalDocs.map((doc) => (
              <DocUploadSlot
                key={doc.key}
                docKey={doc.key}
                label={doc.label}
                required={doc.required}
                uploadedFile={docFiles[doc.key] || null}
                onChange={onDocChange}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * useDocUploads — local state hook for managing document file uploads.
 * Use this alongside DocUploadSection in the listing view.
 */
export const useDocUploads = () => {
  const [docFiles, setDocFiles] = useState({});
  const handleDocChange = (docKey, file) =>
    setDocFiles((prev) => ({ ...prev, [docKey]: file }));
  const resetDocFiles = () => setDocFiles({});
  return { docFiles, handleDocChange, resetDocFiles };
};

// ── SCSS classes used by DocUploadSection (add to index.scss) ─────────────────
// .al-doc-section        — wrapper
// .al-doc-group          — group (entity-type / conditional)
// .al-doc-group__title   — group heading
// .al-doc-grid           — 2-col grid of slots
// .al-doc-slot           — single slot wrapper
// .al-doc-slot__label    — field label
// .al-doc-slot__req      — required asterisk
// .al-doc-slot__box      — dashed upload area
// .al-doc-slot__box--done — green when file uploaded
// .al-doc-slot__input    — hidden file input
// .al-doc-slot__filename — uploaded filename
// .al-doc-slot__hint     — "Click to upload"
// .al-doc-slot__types    — "PDF, JPG, PNG"
